import React, { useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import Lang from "./Lang";
import PremiumButton from "./PremiumButton";
import "./dialog.css";

/**
 * Modal dialog component using a custom React overlay (no native <dialog>).
 * Provides a modal dialog with built-in footer for Cancel/Confirm actions.
 *
 * @author Forge
 * @date 2026-04-08
 * @version 2.0
 *
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {Function} props.onClose - Called when dialog closes (Cancel, X, backdrop click)
 * @param {Function} [props.onConfirm] - Called when Confirm button clicked
 * @param {string} props.title - Dialog title (translation key or plain text)
 * @param {ReactNode} [props.titleIcon] - Optional icon next to title
 * @param {ReactNode} props.children - Dialog content
 * @param {'sm'|'md'|'lg'|'xl'} [props.size='md'] - Dialog width: sm (400px), md (500px), lg (700px), xl (1100px)
 * @param {boolean} [props.showClose=true] - Show close button in header
 * @param {string} [props.confirmLabel='submit'] - Confirm button label (translation key)
 * @param {string} [props.cancelLabel='cancel'] - Cancel button label (translation key)
 * @param {boolean} [props.confirmDisabled=false] - Disable confirm button
 * @param {boolean} [props.hideFooter=false] - Hide footer entirely
 * @param {boolean} [props.hideCancel=false] - Hide cancel button in footer
 * @param {boolean} [props.danger=false] - Danger variant (red confirm button)
 * @param {string} [props.className=''] - Additional content area classes
 * @param {React.Ref} [props.innerRef=null] - Ref to the dialog's inner container div
 */
export default function Dialog({
  open,
  onClose,
  onConfirm,
  title,
  titleIcon,
  children,
  size = "md",
  showClose = true,
  confirmLabel = "submit",
  cancelLabel = "cancel",
  confirmDisabled = false,
  hideFooter = false,
  hideCancel = false,
  danger = false,
  className = "",
  innerRef = null,
}) {
  const dialogRef = useRef(null);
  const prevActiveElement = useRef(null);

  const sizeWidths = {
    sm: "25rem",
    md: "31.25rem",
    lg: "43.75rem",
    xl: "68.75rem",
  };

  const dialogWidth = sizeWidths[size] || sizeWidths.md;

  const handleClose = useCallback(() => {
    if (prevActiveElement.current) {
      prevActiveElement.current.focus();
    }
    onClose?.();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (onConfirm) {
      onConfirm();
    }
  }, [onConfirm]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    if (open) {
      prevActiveElement.current = document.activeElement;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleClose]);

  const isTranslationKey = (str) => {
    if (!str) return false;
    return !str.includes(" ") && str[0] === str[0].toLowerCase();
  };

  const renderTitle = () => {
    if (!title) return null;
    return isTranslationKey(title) ? <Lang w={title} /> : title;
  };

  const renderCancelButton = () => {
    if (hideCancel) return null;
    return (
      <PremiumButton variant="secondary" size="sm" onClick={handleClose}>
        {isTranslationKey(cancelLabel) ? <Lang w={cancelLabel} /> : cancelLabel}
      </PremiumButton>
    );
  };

  const renderConfirmButton = () => {
    if (!onConfirm) return null;
    return (
      <PremiumButton
        variant={danger ? "danger" : "primary"}
        size="sm"
        onClick={handleConfirm}
        disabled={confirmDisabled}
      >
        {isTranslationKey(confirmLabel) ? <Lang w={confirmLabel} /> : confirmLabel}
      </PremiumButton>
    );
  };

  if (typeof document === "undefined" || !open) return null;

  return createPortal(
    <div className="dialog-backdrop" onClick={handleBackdropClick}>
      <div
        ref={dialogRef}
        className="dialog-inner"
        style={{ maxWidth: dialogWidth }}
        role="dialog"
        aria-modal="true"
      >
        <div ref={innerRef} className="dialog-box">
          <header className="dialog-header">
            <div className="dialog-title-wrapper">
              {titleIcon && <span className="dialog-title-icon">{titleIcon}</span>}
              <h2 className="dialog-title">{renderTitle()}</h2>
            </div>
            {showClose && (
              <button
                type="button"
                className="dialog-close-btn"
                onClick={handleClose}
                aria-label="Close dialog"
              >
                <X size={18} />
              </button>
            )}
          </header>

          <div className={`dialog-content ${className}`}>{children}</div>

          {!hideFooter && (onConfirm || !hideCancel) && (
            <footer className="dialog-footer">
              <div className="dialog-actions">
                {renderCancelButton()}
                {renderConfirmButton()}
              </div>
            </footer>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
